import React, { useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

/**
 * Custom price pin (chic, breton-themed) - black tag with white text + accent dot
 */
function priceIcon(price, isActive) {
  const html = `
    <div class="msb-pin ${isActive ? "msb-pin-active" : ""}">
      <span>${price}€</span>
    </div>`;
  return L.divIcon({
    html,
    className: "msb-pin-wrap",
    iconSize: [62, 28],
    iconAnchor: [31, 28],
    popupAnchor: [0, -30],
  });
}

function ListingPopup({ listing }) {
  const photo = listing.photos?.[0];
  const photoSrc = photo
    ? (photo.startsWith("http") ? photo : `${process.env.REACT_APP_BACKEND_URL}/api/files/${photo}`)
    : null;
  return (
    <div className="w-56" data-testid={`popup-${listing.id}`}>
      <Link to={`/listings/${listing.id}`} className="block group">
        <div className="aspect-[4/3] bg-[#F5F2EC] overflow-hidden mb-2">
          {photoSrc ? (
            <img src={photoSrc} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[10px] uppercase tracking-[0.25em] font-bold text-neutral-500">
              Photos à venir
            </div>
          )}
        </div>
        <div className="text-[9px] uppercase tracking-[0.25em] text-neutral-500 font-bold mb-1">
          {listing.region}
        </div>
        <div className="font-display text-sm font-bold leading-tight line-clamp-2 mb-1">
          {listing.title}
        </div>
        <div className="flex items-center justify-between text-xs mt-2 pt-2 border-t border-black/10">
          <span className="text-neutral-500">{listing.city}</span>
          <span className="font-bold">{listing.price_per_night}€<span className="text-neutral-400 font-normal">/nuit</span></span>
        </div>
      </Link>
    </div>
  );
}

/** Auto-fit map to markers, only when listings or activeId change */
function FitBounds({ points, activeId }) {
  const map = useMap();
  const fittedRef = useRef(false);

  useEffect(() => {
    if (!points.length) return;
    if (activeId) {
      const p = points.find((p) => p.id === activeId);
      if (p) {
        map.flyTo([p.lat, p.lng], 12, { duration: 0.6 });
      }
      return;
    }
    if (fittedRef.current) return;
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 11);
    } else {
      const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 11 });
    }
    fittedRef.current = true;
  }, [points, activeId, map]);
  return null;
}

const BRITTANY_CENTER = [48.2, -3.0];

export default function MapView({ listings = [], activeId, onMarkerClick, height = 600 }) {
  const points = useMemo(
    () => listings.filter((l) => typeof l.lat === "number" && typeof l.lng === "number"),
    [listings]
  );

  // react-leaflet 5+ handles React 19 StrictMode double-mounting cleanly.

  return (
    <div
      className="relative overflow-hidden rounded-lg border border-black/10"
      style={{ height }}
      data-testid="leaflet-map"
    >
      <MapContainer
        center={BRITTANY_CENTER}
        zoom={8}
        scrollWheelZoom={true}
        className="w-full h-full"
        style={{ background: "#F5F2EC", height: "100%", width: "100%" }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
          url="https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png"
          subdomains={["a", "b", "c", "d"]}
        />
        <TileLayer
          attribution=""
          url="https://{s}.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}{r}.png"
          subdomains={["a", "b", "c", "d"]}
          opacity={0.85}
        />
        {points.map((l) => (
          <Marker
            key={l.id}
            position={[l.lat, l.lng]}
            icon={priceIcon(l.price_per_night, activeId === l.id)}
            eventHandlers={{
              click: () => onMarkerClick?.(l.id),
            }}
          >
            <Popup closeButton={true}>
              <ListingPopup listing={l} />
            </Popup>
          </Marker>
        ))}
        <FitBounds points={points} activeId={activeId} />
      </MapContainer>

      {points.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="bg-white/95 px-6 py-4 border border-black/10 text-center max-w-sm">
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-neutral-500 mb-2">Carte de Bretagne</div>
            <p className="text-sm text-neutral-700">Aucune annonce à afficher pour ces critères.</p>
          </div>
        </div>
      )}
    </div>
  );
}
